import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { handleResponse } from '../api/helpers.ts'
import {
	type CreateProductPayload,
	type ProductEntity,
	serviceManager,
	type UpdateProductPayload,
	type UpdateProductStatusPayload,
} from '../api/service-manager.ts'
import { asQueryParam, type Impersonation, useImpersonation } from './impersonation.ts'

export const subscriptionQuery = (impersonation: Impersonation | undefined) => {
	return queryOptions({
		queryKey: ['SUBSCRIPTION', impersonation?.subscriptionId],
		queryFn: () =>
			serviceManager.GET('/service-manager/subscriptions', { params: { query: asQueryParam(impersonation) } }).then(handleResponse),
	})
}
export const useSubscription = () => useSuspenseQuery(subscriptionQuery(useImpersonation()))

export const productsQuery = queryOptions({
	queryKey: ['PRODUCTS'],
	queryFn: () => serviceManager.GET('/service-manager/products').then(handleResponse) as Promise<ProductEntity[]>,
	staleTime: 1000 * 60 * 15,
})

export const customersQuery = queryOptions({
	queryKey: ['CUSTOMERS'],
	queryFn: () => serviceManager.GET('/service-manager/customers').then(handleResponse),
	staleTime: 1000 * 60 * 15,
})

export const useProducts = () => useQuery(productsQuery)

export const useCustomers = () => useQuery(customersQuery)

export const useCreateProduct = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (body: CreateProductPayload) => serviceManager.POST('/service-manager/products', { body }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['PRODUCTS'] })
		},
	})
}

export const useEditProduct = (id: string | undefined) => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (body: UpdateProductPayload) => {
			if (!id) return
			return serviceManager.PATCH('/service-manager/products/{id}', { params: { path: { id } }, body })
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['PRODUCTS'] })
		},
	})
}

export const useUpdateProductStatus = (id: string) => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (status: UpdateProductStatusPayload['status']) => {
			return serviceManager.PATCH('/service-manager/products/{id}/status', {
				params: { path: { id } },
				body: { status },
			})
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['PRODUCTS'] })
		},
	})
}

export const useDeleteProduct = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (id: string) => {
			return serviceManager.DELETE('/service-manager/products/{id}', { params: { path: { id } } })
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['PRODUCTS'] })
		},
	})
}

export const productOfferingsQuery = queryOptions({
	queryKey: ['PRODUCT_OFFERINGS'],
	queryFn: () => serviceManager.GET('/service-manager/product-offerings').then(handleResponse),
	staleTime: 1000 * 60 * 15,
})

export const useProductOfferings = () => useQuery(productOfferingsQuery)
